import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Alert, Card, Badge, Button, Table } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { useStore } from '../../../stores/store';
import { useSetMenu } from '../../../hooks/useSetMenu';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { PageHeader, StyledCard, EmptyState, SectionNavigation } from '../../Common/UI';
import './EvaluationList.css';

const EvaluationList = () => {
  const { t } = useTranslation();
  const { mainStore, evaluationStore } = useStore();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useSetMenu(mainStore.EvaluationMenuType, mainStore.EvaluationMenuNo);

  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        const response = await axiosPrivate.get('/EvaluationInstances/GetStaffEvaluations');

        if (response.data) {
          setEvaluations(response.data);
          evaluationStore.setInstances(response.data);
        }
      } catch (err) {
        setError(err.response?.data?.message || t('Error fetching evaluations'));
        enqueueSnackbar(t('errorLoadingEvaluations'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();

    return () => {
      evaluationStore.setInstances([]);
    };
  }, [axiosPrivate, t, enqueueSnackbar, evaluationStore]);

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusVariant = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'submitted':
      case 'completed':
        return 'success';
      case 'expired':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getFilteredEvaluations = () => {
    switch(activeTab) {
      case 'pending':
        return evaluations.filter(e => e.instanceStatusName?.toLowerCase() === 'pending');
      case 'submitted':
        return evaluations.filter(e => e.instanceStatusName?.toLowerCase() === 'submitted' ||
                                     e.instanceStatusName?.toLowerCase() === 'completed');
      default:
        return evaluations;
    }
  };

  const filteredEvaluations = getFilteredEvaluations();

  const counts = {
    all: evaluations.length,
    pending: evaluations.filter(e => e.instanceStatusName?.toLowerCase() === 'pending').length,
    submitted: evaluations.filter(e => e.instanceStatusName?.toLowerCase() === 'submitted' ||
                                     e.instanceStatusName?.toLowerCase() === 'completed').length
  };

  const sections = [
    { key: 'all', label: t('allEvaluations'), icon: 'list-ul', badge: counts.all },
    { key: 'pending', label: t('pendingEvaluations'), icon: 'clock-history', badge: counts.pending, badgeVariant: 'warning' },
    { key: 'submitted', label: t('submittedEvaluations'), icon: 'check-circle', badge: counts.submitted, badgeVariant: 'success' }
  ];

  if (loading) {
    return (
      <div className="evaluation-container">
        <div className="text-center py-5">
          <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
          <p className="mt-3 text-muted">{t('loadingEvaluations')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="evaluation-container">
        <PageHeader
          icon="clipboard-check"
          title={t('evaluations')}
          subtitle={t('manageYourEvaluations')}
        />
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="evaluation-container">
      <PageHeader
        icon="clipboard-check"
        title={t('evaluations')}
        subtitle={t('manageYourEvaluations')}
      />

      {}
      <StyledCard className="mb-4 info-card" hoverable={false}>
        <Card.Body>
          <div className="d-flex align-items-center">
            <div className="info-icon-wrapper me-3">
              <i className="bi bi-info-circle-fill"></i>
            </div>
            <div>
              <h6 className="mb-1">{t('evaluationSystemInfo')}</h6>
              <p className="mb-0 text-muted">
                {t('evaluationsDescription')}
              </p>
            </div>
          </div>
        </Card.Body>
      </StyledCard>

      {}
      <SectionNavigation
        sections={sections}
        activeSection={activeTab}
        onSectionChange={setActiveTab}
      />

      {}
      {filteredEvaluations.length === 0 ? (
        <EmptyState
          icon="clipboard-x"
          title={activeTab === 'pending' ? t('noPendingEvaluations') :
                 activeTab === 'submitted' ? t('noSubmittedEvaluations') :
                 t('noEvaluationsAvailable')}
          message={t('checkBackLaterForNewEvaluations')}
        />
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {filteredEvaluations.map(evaluation => (
            <Col key={evaluation.id}>
              <StyledCard className="h-100 evaluation-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h6 className="evaluation-title mb-0">
                      {evaluation.evaluationTemplateName}
                    </h6>
                    <Badge bg={getStatusVariant(evaluation.instanceStatusName)}>
                      {evaluation.instanceStatusName}
                    </Badge>
                  </div>

                  <div className="evaluation-details">
                    {evaluation.subjectName && (
                      <div className="detail-item">
                        <i className="bi bi-book me-2"></i>
                        <span className="detail-label">{t('subject')}:</span>
                        <span className="detail-value">{evaluation.subjectName}</span>
                      </div>
                    )}

                    {evaluation.submittedDate && (
                      <div className="detail-item">
                        <i className="bi bi-calendar-check me-2"></i>
                        <span className="detail-label">{t('submittedDate')}:</span>
                        <span className="detail-value">{formatDate(evaluation.submittedDate)}</span>
                      </div>
                    )}

                    {evaluation.dueDate && (
                      <div className="detail-item">
                        <i className="bi bi-calendar-event me-2"></i>
                        <span className="detail-label">{t('dueDate')}:</span>
                        <span className="detail-value">{formatDate(evaluation.dueDate)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <Link
                      to={`/Evaluation/View/${evaluation.id}`}
                      className="btn btn-danger btn-sm w-100"
                    >
                      <i className="bi bi-eye me-2"></i>
                      {t('viewEvaluation')}
                    </Link>
                  </div>
                </Card.Body>
              </StyledCard>
            </Col>
          ))}
        </Row>
      )}

      {}
      {}
    </div>
  );
};

export default EvaluationList;